import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { isBusinessError } from './businessErrorHandler';

/**
 * Extracts error code from 422 business logic errors
 * Returns the code if present, otherwise null
 */
export const extractErrorCode = (error: unknown): string | null => {
  // Handle AxiosError with response data
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    
    // Check if it's a 422 error with code field
    if (error.response?.status === 422 && responseData && typeof responseData === 'object' && 'code' in responseData) {
      const code = responseData.code;
      if (typeof code === 'string' && code.trim()) {
        return code;
      }
    }
  }
  
  // Handle error objects with response property (non-AxiosError)
  if (error && typeof error === 'object' && 'response' in error) {
    const errorWithResponse = error as { response?: { status?: number; data?: { code?: string } | string } };
    const response = errorWithResponse.response;
    
    if (response?.status === 422 && response.data && typeof response.data === 'object' && 'code' in response.data) {
      const code = response.data.code;
      if (typeof code === 'string' && code.trim()) {
        return code;
      }
    }
  }
  
  return null;
};

/**
 * Checks if a string looks like an error code
 * Error codes are typically all uppercase with underscores (e.g., NO_ACTIVE_SUBSCRIPTION)
 */
export const isErrorCode = (str: string): boolean => {
  // Error codes are typically all uppercase letters, numbers, and underscores
  // and match known patterns from the API
  return /^(NO_|MAX_|SUBSCRIPTION_)[A-Z0-9_]+$/.test(str);
};

/**
 * Translates an error code to a user-friendly message using i18n
 * @param code - The error code to translate
 * @param t - The i18n translation function
 * @returns Translated error message
 */
export const translateErrorCode = (code: string, t: TFunction): string => {
  return t(`errorCodes.${code}`, { 
    defaultValue: `An error occurred: ${code}` 
  });
};

/**
 * Extracts and translates error message from various error types
 * For 422 errors with codes, translates the code using i18n
 * For other errors, uses standard message extraction
 * @param error - The error object
 * @param t - The i18n translation function (optional, required for 422 code translation)
 * @returns Translated or extracted error message
 */
export const getTranslatedErrorMessage = (error: unknown, t?: TFunction): string => {
  // For 422 errors, try to extract and translate code first
  if (isBusinessError(error)) {
    const code = extractErrorCode(error);
    if (code && t) {
      return translateErrorCode(code, t);
    }
    // If code exists but no t function, return code as fallback
    if (code) {
      return `An error occurred: ${code}`;
    }
  }
  
  // For non-422 errors or 422 errors without code, use standard message extraction
  return extractErrorMessage(error);
};

/**
 * Extracts error message from various error types, prioritizing backend response messages
 * Note: For 422 errors with codes, use getTranslatedErrorMessage() with i18n function for proper translation
 * This function will extract the code as a fallback for 422 errors if no message is present
 */
export const extractErrorMessage = (error: unknown): string => {
  // Handle AxiosError with response data
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    
    // Check if response data has a message field
    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
      const message = responseData.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    
    // For 422 errors, check for code field as fallback (if no message)
    if (error.response?.status === 422 && responseData && typeof responseData === 'object' && 'code' in responseData) {
      const code = responseData.code;
      if (typeof code === 'string' && code.trim()) {
        return code; // Return code - will be translated by components with i18n access
      }
    }
    
    // Check if response data is a string
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    
    // Fallback to AxiosError message
    if (error.message) {
      return error.message;
    }
  }
  
  // Handle error objects with response property (non-AxiosError)
  if (error && typeof error === 'object' && 'response' in error) {
    const errorWithResponse = error as { response?: { data?: { message?: string } | string } };
    const responseData = errorWithResponse.response?.data;
    
    if (responseData) {
      if (typeof responseData === 'object' && 'message' in responseData) {
        const message = responseData.message;
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      } else if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
    }
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Creates a standardized error object with extracted message
 * Preserves AxiosError status information for retry and error handling logic
 */
export const createStandardizedError = (error: unknown): Error => {
  const message = extractErrorMessage(error);
  const standardizedError = new Error(message);
  
  // Preserve AxiosError status information for error handling logic
  if (error instanceof AxiosError && error.response?.status) {
    (standardizedError as any).response = {
      status: error.response.status,
      statusText: error.response.statusText,
    };
    (standardizedError as any).isAxiosError = true;
  }
  
  return standardizedError;
};

/**
 * Type guard to check if error has response data with message
 */
export const hasResponseMessage = (error: unknown): error is { response: { data: { message: string } } } => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data !== null &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  );
};

/**
 * Checks if an error is a 422 business logic error
 * These errors are handled by the business error dialog, so toasts should not be shown
 */
export const isBusinessLogicError = (error: unknown): boolean => {
  return isBusinessError(error);
};

/**
 * Shows an error toast, but skips showing toast for 422 errors
 * since those are already handled by the business error dialog
 * 
 * @param error - The error object
 * @param toast - The toast object from 'sonner' (e.g., `toast` from `import { toast } from 'sonner'`)
 * @param defaultMessage - Optional default message if error extraction fails
 */
export const showErrorToast = (
  error: unknown,
  toast: { error: (message: string) => void },
  defaultMessage?: string
): void => {
  // Don't show toast for 422 errors - they're handled by the dialog
  if (isBusinessLogicError(error)) {
    return;
  }
  
  const message = extractErrorMessage(error) || defaultMessage || 'An error occurred';
  toast.error(message);
}; 